import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

/**
 * Legacy /schedule-meeting redirect. Preserves query string (date, time,
 * timezone, source, utm_*) so the /book-demo flow can prefill/attribute.
 */
const ScheduleMeetingRedirect = () => {
  const location = useLocation();
  const { getLocalizedPath } = useLocale();
  return (
    <Navigate
      to={`${getLocalizedPath('/book-demo')}${location.search}${location.hash}`}
      replace
    />
  );
};
import { LocaleProvider, useLocale } from '@/contexts/LocaleContext';
import { SupportedLocale } from '@/i18n/index';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { SecureRoute } from '@/components/SecureRoute';

import Index from '@/pages/Index';
import LocaleNavigate from '@/components/LocaleNavigate';

// Lazy load marketing pages
const About = lazy(() => import("@/pages/About"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PlanComparison = lazy(() => import("@/pages/PlanComparison"));
const UseCases = lazy(() => import("@/pages/UseCases"));
const Templates = lazy(() => import("@/pages/Templates"));
const ProgramPage = lazy(() => import("@/pages/ProgramPage"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const BookDemo = lazy(() => import("@/pages/BookDemo"));
const DemoDays = lazy(() => import("@/pages/DemoDays"));
const DemoDayEpisode = lazy(() => import("@/pages/DemoDayEpisode"));
const DemoDayRegistered = lazy(() => import("@/pages/DemoDayRegistered"));
const Webinars = lazy(() => import("@/pages/Webinars"));
const Events = lazy(() => import("@/pages/Events"));
const AdvancedAnalytics = lazy(() => import("@/pages/AdvancedAnalytics"));
const RiskAnalyticsRedirect = lazy(() => import("@/components/RiskAnalyticsRedirect"));
const EnterpriseRisk = lazy(() => import("@/pages/EnterpriseRisk"));
const EnterpriseAudit = lazy(() => import("@/pages/EnterpriseAudit"));
const ROICalculatorPage = lazy(() => import("@/pages/ROICalculatorPage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const DoNotSell = lazy(() => import("@/pages/DoNotSell"));
const DataSecurity = lazy(() => import("@/pages/DataSecurity"));
const TrustCenter = lazy(() => import("@/pages/TrustCenter"));
const Compliance = lazy(() => import("@/pages/Compliance"));
const ComplianceBlueprint = lazy(() => import("@/pages/ComplianceBlueprint"));
const Contact = lazy(() => import("@/pages/Contact"));
const SupportContact = lazy(() => import("@/pages/SupportContact"));
const DiscoveryCall = lazy(() => import("@/pages/DiscoveryCall"));
const DSARPage = lazy(() => import("@/pages/DSARPage"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const Insights = lazy(() => import("@/pages/Insights"));
const Article = lazy(() => import("@/pages/Article"));
const Guide = lazy(() => import("@/pages/Guide"));
const Checklist = lazy(() => import("@/pages/Checklist"));
const AssessmentPage = lazy(() => import("@/pages/AssessmentPage"));
const OurApproach = lazy(() => import("@/pages/OurApproach"));
const HikersMindset = lazy(() => import("@/pages/HikersMindset"));

const RIPEAssessmentPage = lazy(() => import("@/pages/RIPEAssessmentPage"));
const Resources = lazy(() => import("@/pages/Resources"));
const ManagedServices = lazy(() => import("@/pages/ManagedServices"));
const Features = lazy(() => import("@/pages/Features"));
const PlatformTour = lazy(() => import("@/pages/PlatformTour"));
const TourVideoPage = lazy(() => import("@/pages/TourVideoPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Enterprise = lazy(() => import("@/pages/Enterprise"));
const ReadinessAssessment = lazy(() => import("@/pages/ReadinessAssessment"));
const BrandGuidelines = lazy(() => import("@/pages/BrandGuidelines"));
const CareerTransition = lazy(() => import("@/pages/CareerTransition"));
const Careers = lazy(() => import("@/pages/Careers"));
const CareersDirectorOfTechnology = lazy(() => import("@/pages/CareersDirectorOfTechnology"));
const WhyCsquare = lazy(() => import("@/pages/WhyCsquare"));

const Partners = lazy(() => import("@/pages/Partners"));
const Newsroom = lazy(() => import("@/pages/Newsroom"));
const PressKit = lazy(() => import("@/pages/PressKit"));
const ContactSales = lazy(() => import("@/pages/ContactSales"));
const WhatsNew = lazy(() => import("@/pages/WhatsNew"));
const GlobalCoverage = lazy(() => import("@/pages/GlobalCoverage"));
const SeeWhatApplies = lazy(() => import("@/pages/SeeWhatApplies"));
const Dora = lazy(() => import("@/pages/Dora"));
const Explorer = lazy(() => import("@/pages/Explorer"));
const AudienceLanding = lazy(() => import("@/pages/AudienceLanding"));
const FrameworkLibrary = lazy(() => import("@/pages/FrameworkLibrary"));
const SizeSegmentLanding = lazy(() => import("@/pages/SizeSegmentLanding"));
const Upgrade = lazy(() => import("@/pages/Upgrade"));
const UpgradeSuccess = lazy(() => import("@/pages/UpgradeSuccess"));
const Billing = lazy(() => import("@/pages/Billing"));
const AIGrcPlatform = lazy(() => import("@/pages/AIGrcPlatform"));
const AutomatedControlMapping = lazy(() => import("@/pages/AutomatedControlMapping"));
const FrameworkMapping = lazy(() => import("@/pages/FrameworkMapping"));
const ControlApplicabilityWizard = lazy(() => import("@/pages/ControlApplicabilityWizard"));
const IntegrationsContinuousMonitoring = lazy(() => import("@/pages/IntegrationsContinuousMonitoring"));
const RegulatedEnterprises = lazy(() => import("@/pages/RegulatedEnterprises"));
const RiskManagement = lazy(() => import("@/pages/RiskManagement"));
const AutomatedGapAnalysis = lazy(() => import("@/pages/AutomatedGapAnalysis"));
const ThirdPartyRisk = lazy(() => import("@/pages/ThirdPartyRisk"));
const QuestionnaireAutomation = lazy(() => import("@/pages/QuestionnaireAutomation"));
const SignupExplorer = lazy(() => import("@/pages/SignupExplorer"));
const Welcome = lazy(() => import("@/pages/Welcome"));

const PageLoadingFallback = () => (
  <div className="min-h-screen bg-muted/30 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const LP = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
);

interface MarketingLocaleRoutesProps {
  locale: SupportedLocale;
}

const MarketingRoutes = () => (
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/features" element={<LP><Features /></LP>} />
    <Route path="/platform-tour" element={<LP><PlatformTour /></LP>} />
    <Route path="/platform-tour/:slug" element={<LP><TourVideoPage /></LP>} />
    <Route path="/whats-new" element={<LP><WhatsNew /></LP>} />
    <Route path="/about" element={<LP><About /></LP>} />
    {/* Team page unpublished for now; keep shared links alive. */}
    <Route path="/team" element={<LocaleNavigate to="/about" replace />} />
    <Route path="/pricing" element={<LP><ErrorBoundary fallback={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><h2 className="text-xl font-semibold mb-2">Pricing Page Error</h2><p className="text-muted-foreground">Please try refreshing the page</p></div></div>}><Pricing /></ErrorBoundary></LP>} />
    <Route path="/plan-comparison" element={<LP><PlanComparison /></LP>} />
    <Route path="/signup" element={<LP><SignupExplorer /></LP>} />
    <Route path="/start-free" element={<LP><SignupExplorer /></LP>} />
    <Route path="/welcome" element={<LP><Welcome /></LP>} />
    <Route path="/signup-explorer" element={<LocaleNavigate to="/signup" replace />} />
    <Route path="/use-cases" element={<LP><UseCases /></LP>} />
    <Route path="/use-cases/:framework" element={<LP><UseCases /></LP>} />
    <Route path="/templates" element={<LP><Templates /></LP>} />
    <Route path="/insights" element={<LP><Insights /></LP>} />
    <Route path="/insights/article/:id" element={<LP><Article /></LP>} />
    <Route path="/insights/guide/:id" element={<LP><Guide /></LP>} />
    <Route path="/insights/checklist/:id" element={<LP><Checklist /></LP>} />
    <Route path="/integrations" element={<LP><Integrations /></LP>} />
    <Route path="/automated-compliance" element={<LocaleNavigate to="/features" replace />} />
    {/* Retired consulting-era services page */}
    <Route path="/compliance" element={<LocaleNavigate to="/features" replace />} />

    <Route path="/advanced-analytics" element={<LP><AdvancedAnalytics /></LP>} />
    <Route path="/risk-analytics" element={<LP><RiskAnalyticsRedirect /></LP>} />
    <Route path="/enterprise-risk" element={<LP><EnterpriseRisk /></LP>} />
    <Route path="/enterprise-audit" element={<LP><EnterpriseAudit /></LP>} />
    <Route path="/roi-calculator" element={<LP><ROICalculatorPage /></LP>} />
    <Route path="/roi" element={<LP><ROICalculatorPage /></LP>} />
    {/* One argument page per pack that has one. /privacy stays the legal
        policy, so the privacy argument lives under /programs and never buries
        it. The two former hand-built wedge URLs keep working. */}
    <Route path="/programs/:pack" element={<LP><ProgramPage /></LP>} />
    <Route path="/privacy-program" element={<LocaleNavigate to="/programs/privacy-global" replace />} />
    <Route path="/federal-grants" element={<LocaleNavigate to="/programs/federal-grants" replace />} />
    <Route path="/privacy" element={<LP><PrivacyPolicy /></LP>} />
    {/* /privacy is canonical. /privacy-policy rendered the same component, so
        both URLs were in the sitemap and Google saw one page twice. It now
        collapses client-side; vercel.json 301s it at the edge. */}
    <Route path="/privacy-policy" element={<LocaleNavigate to="/privacy" replace />} />
    <Route path="/cookie-policy" element={<LP><CookiePolicy /></LP>} />
    <Route path="/do-not-sell" element={<LP><DoNotSell /></LP>} />
    <Route path="/terms" element={<LP><Terms /></LP>} />
    <Route path="/faq" element={<LP><FAQ /></LP>} />
    <Route path="/cookies" element={<LP><CookiePolicy /></LP>} />
    <Route path="/data-security" element={<LP><DataSecurity /></LP>} />
    <Route path="/trust" element={<LP><TrustCenter /></LP>} />
    <Route path="/trust-center" element={<LocaleNavigate to="/trust" replace />} />
    <Route path="/compliance" element={<LP><Compliance /></LP>} />
    <Route path="/compliance-blueprint" element={<LP><ComplianceBlueprint /></LP>} />
    <Route path="/dsar" element={<LP><DSARPage /></LP>} />
    <Route path="/unsubscribe" element={<LP><Unsubscribe /></LP>} />
    <Route path="/contact" element={<LP><Contact /></LP>} />
    <Route path="/support" element={<LP><SupportContact /></LP>} />
    <Route path="/discovery-call" element={<LP><DiscoveryCall /></LP>} />
    <Route path="/book-demo" element={<LP><BookDemo /></LP>} />
    <Route path="/schedule-meeting" element={<ScheduleMeetingRedirect />} />
    <Route path="/demo-days" element={<LP><DemoDays /></LP>} />
    <Route path="/demo-days/:slug/registered" element={<LP><DemoDayRegistered /></LP>} />
    <Route path="/demo-days/:slug" element={<LP><DemoDayEpisode /></LP>} />
    <Route path="/webinars" element={<LP><Webinars /></LP>} />
    {/* Merged into /platform-tour Sep 1 2026: one tour, video first. */}
    <Route path="/product-tour" element={<LocaleNavigate to="/platform-tour" replace />} />
    <Route path="/events" element={<LP><Events /></LP>} />
    <Route path="/platform-demo" element={<LocaleNavigate to="/features" replace />} />
    <Route path="/assessment" element={<LP><AssessmentPage /></LP>} />
    <Route path="/our-approach" element={<LP><OurApproach /></LP>} />
    <Route path="/hikers-mindset" element={<LP><HikersMindset /></LP>} />
    <Route path="/methodology" element={<LocaleNavigate to="/our-approach" replace />} />
    <Route path="/platform" element={<LocaleNavigate to="/our-approach" replace />} />
    <Route path="/ceo" element={<LocaleNavigate to="/about" replace />} />
    <Route path="/about/jacques-nack" element={<LocaleNavigate to="/about" replace />} />
    <Route path="/ripe-assessment" element={<LP><RIPEAssessmentPage /></LP>} />
    <Route path="/resources" element={<LP><Resources /></LP>} />
    <Route path="/managed-services" element={<LP><ManagedServices /></LP>} />
    <Route path="/enterprise" element={<LP><Enterprise /></LP>} />
    <Route path="/readiness" element={<LP><ReadinessAssessment /></LP>} />
    <Route path="/readiness/:framework" element={<LP><ReadinessAssessment /></LP>} />
    <Route path="/readiness-assessment" element={<LP><ReadinessAssessment /></LP>} />
    <Route path="/readiness-assessment/:framework" element={<LP><ReadinessAssessment /></LP>} />
    <Route path="/global-coverage" element={<LP><GlobalCoverage /></LP>} />
    <Route path="/see-what-applies" element={<LP><SeeWhatApplies /></LP>} />
    <Route path="/coverage/apply" element={<LocaleNavigate to="/see-what-applies" replace />} />
    <Route path="/dora" element={<LP><Dora /></LP>} />
    <Route path="/brand-guidelines" element={<LP><BrandGuidelines /></LP>} />
    <Route path="/career-transition" element={<LP><CareerTransition /></LP>} />
    <Route path="/careers" element={<LP><Careers /></LP>} />
    <Route path="/careers/director-of-technology" element={<LP><CareersDirectorOfTechnology /></LP>} />
    <Route path="/why-csquare" element={<LP><WhyCsquare /></LP>} />
    <Route path="/partners" element={<LP><Partners /></LP>} />
    <Route path="/newsroom" element={<LP><Newsroom /></LP>} />
    <Route path="/press-kit" element={<LP><PressKit /></LP>} />
    <Route path="/contact-sales" element={<LP><ContactSales /></LP>} />
    <Route path="/guides" element={<LocaleNavigate to="/insights" replace />} />
    <Route path="/start-your-journey" element={<LocaleNavigate to="/pricing" replace />} />
    {/* Size-segment landing pages must precede /for/:audience (Track 4) */}
    <Route path="/for/small-business" element={<LP><SizeSegmentLanding /></LP>} />
    <Route path="/for/mid-market" element={<LP><SizeSegmentLanding /></LP>} />
    {/* Audience landing pages (FRD-EXPLORER-WEB-001 v0.6, WEB-FR-002) */}
    {/* Life sciences uses the healthcare page. Do not stand up a second industry URL. */}
    <Route path="/for/life-sciences" element={<LocaleNavigate to="/for/healthcare" replace />} />
    <Route path="/for/:audience" element={<LP><AudienceLanding /></LP>} />
    {/* Framework Library (Track 1) */}
    <Route path="/framework-library" element={<LP><FrameworkLibrary /></LP>} />
    {/* Explorer deprecated → /for/saas */}
    <Route path="/explorer" element={<LocaleNavigate to="/for/saas" replace />} />
    <Route path="/upgrade/success" element={<LP><UpgradeSuccess /></LP>} />
    <Route path="/upgrade/:tier" element={<LP><Upgrade /></LP>} />
    <Route path="/billing" element={<LP><Billing /></LP>} />
    <Route path="/ai-grc-platform" element={<LP><AIGrcPlatform /></LP>} />
    <Route path="/automated-control-mapping" element={<LP><AutomatedControlMapping /></LP>} />
    <Route path="/automated-control-mapping/:framework" element={<LP><FrameworkMapping /></LP>} />
    <Route path="/control-applicability-wizard" element={<LP><ControlApplicabilityWizard /></LP>} />
    <Route path="/integrations/continuous-monitoring" element={<LP><IntegrationsContinuousMonitoring /></LP>} />
    <Route path="/regulated-enterprises" element={<LP><RegulatedEnterprises /></LP>} />
    <Route path="/risk-management" element={<LP><RiskManagement /></LP>} />
    <Route path="/automated-gap-analysis" element={<LP><AutomatedGapAnalysis /></LP>} />
    <Route path="/third-party-risk" element={<LP><ThirdPartyRisk /></LP>} />
    <Route path="/questionnaire-automation" element={<LP><QuestionnaireAutomation /></LP>} />
    <Route path="/audit" element={<LP><EnterpriseAudit /></LP>} />
    {/* Deprecated locale catch-alls, redirect to en-US equivalent */}
    {/* A doubled locale prefix self-heals before it reaches the 404. Google has
        /fr-FR/en-GB/... indexed because the locale fallback used to stack the
        reader's locale on top of a retired one; the host now 301s these, but
        this keeps them working the moment the app loads, including in dev and
        on any host whose redirect rules are not live yet. */}
    <Route path=":inner/*" element={<StripNestedLocale />} />
    <Route path="*" element={<LP><NotFound /></LP>} />
  </Routes>
);

/**
 * Drops a second locale-shaped segment: /fr-FR/en-GB/pricing -> /fr-FR/pricing.
 * Only fires when the segment really looks like a locale, so a real page whose
 * slug happens to sit at that position still routes normally.
 */
const StripNestedLocale = () => {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  // [locale, maybeLocale, ...rest]
  const isNested = segments.length >= 2 && /^[a-z]{2}-[A-Z]{2}$/.test(segments[1]);
  if (!isNested) return <LP><NotFound /></LP>;
  const rest = segments.slice(2);
  const target = `/${segments[0]}${rest.length ? `/${rest.join('/')}` : ''}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
};

const MarketingLocaleRoutes: React.FC<MarketingLocaleRoutesProps> = ({ locale }) => {
  return (
    <LocaleProvider currentLocale={locale}>
      <MarketingRoutes />
    </LocaleProvider>
  );
};

export default MarketingLocaleRoutes;
